import { FC, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ReactComponent as ReloadIcon } from 'src/assets/svg/Reload.svg'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { ConversationCategories } from 'src/components/ConversationCategories'
import { ConversationCategorization } from 'src/components/ConversationCategorization'
import { ErrorBoundary } from 'src/components/ErrorBoundary'
import { FilterPeriod } from 'src/constants/filterPeriods/enums/filterPeriod.enum'
import { MessageVolumeTrends } from 'src/components/MessageVolumeTrends'
import {
  InsightFilterTab,
  SmartInsightsAIRecommendations,
} from 'src/charts/SmartInsightsAIRecommendations'
import { useGetInboxSmartInsightsQuery } from 'src/reactQuery/useGetInboxSmartInsightsQuery'

const INBOX_INSIGHT_FILTER_TABS: InsightFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'customer_intent_signals', label: 'Customer Intent Signals' },
  { key: 'channel_insights', label: 'Channel Insights' },
  { key: 'automation_opportunities', label: 'Automation Opportunities' },
  { key: 'content_recommendations', label: 'Content Recommendations' },
  { key: 'crisis_warnings', label: 'Crisis Warnings' },
]
import { OperatorAnalytics } from 'src/components/OperatorAnalytics'
import { OperatorPerformanceBreakdown } from 'src/components/OperatorPerformanceBreakdown'
import { ResponseTimeAnalysis } from 'src/components/ResponseTimeAnalysis'
import { TopPhrases } from 'src/components/TopPhrases'
import { INBOX_ANALYSIS_ROUTE } from 'src/constants/routes'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { useGetInboxActiveDateRangeQuery } from 'src/reactQuery/useGetInboxActiveDateRangeQuery'
import { useGetInboxAvailablePlatformsQuery } from 'src/reactQuery/useGetInboxAvailablePlatformsQuery'
import { useGetInboxAnalysisPagesQuery } from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import { useGetMessageVolumeTrendsQuery } from 'src/reactQuery/useGetMessageVolumeTrendsQuery'
import { useGetOperatorPerformanceQuery } from 'src/reactQuery/useGetOperatorPerformanceQuery'
import { analyticsApi } from 'src/services/analyticsApi'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { TopCustomerIntents } from 'src/components/TopCustomerIntents'
import { InboxAnalysisOverview } from './InboxAnalysisOverview'
import { InboxPreview } from './InboxPreview'
import { MetaInsights } from './MetaInsights'
import classes from './inboxAnalysisDetail.module.scss'

export const InboxAnalysisDetail: FC = () => {
  const { pageId } = useParams<{ pageId: string }>()
  const [volumePeriod, setVolumePeriod] = useState<
    'overall' | 'daily' | 'weekly' | 'monthly'
  >('overall')
  const [operatorPlatform, setOperatorPlatform] = useState<string>('all')

  // Get date range from query params (uses the global date picker in TopBar)
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  // Preserve the active date filter when navigating back to the list
  const backURL = useCustomLink(INBOX_ANALYSIS_ROUTE)
  const dates = getDateRangeByQueryParams(queryParams)
  const isLifetime = queryParams.period === FilterPeriod.LIFETIME

  // Fetch inbox-specific active date range for lifetime mode
  const { data: inboxDateRange } = useGetInboxActiveDateRangeQuery(
    isLifetime ? pageId : undefined
  )

  // For lifetime, use inbox-specific date range; otherwise use global dates
  const effectiveDates = useMemo(() => {
    if (isLifetime && inboxDateRange?.firstDate && inboxDateRange?.lastDate) {
      return {
        end: inboxDateRange.lastDate,
        start: inboxDateRange.firstDate,
      }
    }
    return dates
  }, [isLifetime, inboxDateRange, dates])

  const dateRange = useMemo(() => {
    return {
      fromDate: effectiveDates?.start,
      toDate: effectiveDates?.end,
    }
  }, [effectiveDates?.start, effectiveDates?.end])

  // Fetch inbox analysis data from API
  const {
    data,
    isLoading,
    refetch: refetchPages,
  } = useGetInboxAnalysisPagesQuery(dateRange)

  // Find the current page from the data
  const currentPage = useMemo(() => {
    if (!data?.pages) return null
    return data.pages.find((page) => page.id === pageId)
  }, [data?.pages, pageId])

  // Get page name for the title
  const pageTitle = currentPage?.name || 'Page Details'

  // Fetch message volume trends data
  const {
    data: volumeData,
    isLoading: volumeLoading,
    refetch: refetchVolume,
  } = useGetMessageVolumeTrendsQuery({
    fromDate: effectiveDates?.start,
    pageId: pageId || '',
    period: volumePeriod,
    toDate: effectiveDates?.end,
  })

  // Fetch operator performance data
  const {
    data: operatorData,
    isLoading: operatorLoading,
    refetch: refetchOperator,
  } = useGetOperatorPerformanceQuery({
    fromDate: effectiveDates?.start,
    pageId: pageId || '',
    platforms: operatorPlatform,
    toDate: effectiveDates?.end,
  })

  // Platforms that actually have data for this page (drives the platform tabs
  // in the operator-performance widget so it doesn't list absent platforms).
  const { data: availablePlatforms } = useGetInboxAvailablePlatformsQuery({
    fromDate: effectiveDates?.start,
    pageId: pageId || undefined,
    toDate: effectiveDates?.end,
  })

  // "Download full report" — the chart shows only the top 6 operators, so this
  // pulls the full list (all operators, same date/platform filters) as CSV.
  const handleDownloadOperatorReport = async () => {
    if (!pageId) return
    const { data } = await analyticsApi().get(
      '/v1_private/inbox-analysis/operator-performance/report',
      {
        params: {
          fromDate: effectiveDates?.start,
          pageId,
          ...(operatorPlatform && operatorPlatform !== 'all'
            ? { platforms: operatorPlatform }
            : {}),
          toDate: effectiveDates?.end,
        },
        responseType: 'blob',
      }
    )
    const url = URL.createObjectURL(data as Blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `operator-performance-${
      new Date().toISOString().split('T')[0]
    }.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  // Fetch smart insights for this page
  const { data: smartInsightsData, isLoading: smartInsightsLoading } =
    useGetInboxSmartInsightsQuery({
      fromDate: effectiveDates?.start,
      pageId: pageId || '',
      toDate: effectiveDates?.end,
    })

  // Empty-state reason for this page, driven by the backend's `emptyReason`
  // (the same field the connected-pages cards use). 'no_data' => analysis ran
  // but there are no conversations in the selected range. Fall back to the old
  // empty heuristic if the field is absent (e.g. a stale cached response).
  const emptyReason = useMemo<'analyzing' | 'no_data' | null>(() => {
    if (!currentPage) return null
    return (
      currentPage.emptyReason ??
      (currentPage.messages.total === 0 && currentPage.topOperators.length === 0
        ? 'no_data'
        : null)
    )
  }, [currentPage])

  // Refetch data when date range changes (including lifetime selection)
  useEffect(() => {
    refetchPages()
    refetchVolume()
    refetchOperator()
  }, [
    effectiveDates?.start,
    effectiveDates?.end,
    refetchPages,
    refetchVolume,
    refetchOperator,
  ])

  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.content}>
          <div className={classes.placeholder}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <BackNavigationButton title={pageTitle} url={backURL} />
      </div>

      {!currentPage && (
        <div className={classes.emptyState}>
          <p>Page not found</p>
        </div>
      )}

      {currentPage && emptyReason === 'no_data' && (
        <div className={classes.analyzingState}>
          <h2 className={classes.analyzingTitle}>No Conversations Found</h2>
        </div>
      )}

      {currentPage && emptyReason === 'analyzing' && (
        <div className={classes.analyzingState}>
          <div className={classes.analyzingIcon}>
            <ReloadIcon />
          </div>
          <h2 className={classes.analyzingTitle}>
            We&apos;re Analyzing Your Data
          </h2>
          <p className={classes.analyzingMessage}>
            We&apos;re analyzing your conversations to uncover insights,
            sentiment, and key trends across your data.
          </p>
          <p className={classes.analyzingSubMessage}>
            Data usually appears in a few hours.
          </p>
        </div>
      )}

      {currentPage && !emptyReason && (
        <>
          <ErrorBoundary label="Smart Insights">
            <SmartInsightsAIRecommendations
              className={classes.smartInsightsCard}
              data={
                smartInsightsData
                  ? {
                      hasInsights: smartInsightsData.hasInsights,
                      insights: smartInsightsData.insights as any,
                    }
                  : undefined
              }
              filterTabs={INBOX_INSIGHT_FILTER_TABS}
              homePageVariant
              isLoading={smartInsightsLoading}
              modalMetricsVariant="inbox"
            />
          </ErrorBoundary>

          <ErrorBoundary label="Overview">
            <InboxAnalysisOverview
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Inbox Preview">
            <InboxPreview
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Conversation Categorization">
            <ConversationCategorization
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Top Customer Intents">
            <TopCustomerIntents
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Insights Summary">
            <MetaInsights
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Message Volume Trends">
            <MessageVolumeTrends
              data={volumeData}
              isLoading={volumeLoading}
              onPeriodChange={setVolumePeriod}
              period={volumePeriod}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Operator Performance Breakdown">
            <OperatorPerformanceBreakdown
              activePlatform={operatorPlatform}
              availablePlatforms={availablePlatforms}
              data={operatorData}
              isLoading={operatorLoading}
              onDownloadReport={handleDownloadOperatorReport}
              onPlatformChange={setOperatorPlatform}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Operator Analytics">
            <OperatorAnalytics
              availablePlatforms={availablePlatforms}
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Response Time Analysis">
            <ResponseTimeAnalysis
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Frequently Used Phrases">
            <TopPhrases
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>

          <ErrorBoundary label="Conversation Categories">
            <ConversationCategories
              fromDate={effectiveDates?.start}
              pageId={pageId || ''}
              toDate={effectiveDates?.end}
            />
          </ErrorBoundary>
        </>
      )}
    </div>
  )
}
